import { Link } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import { SERVICES } from "@/data";
import { Reveal } from "@/components/motion";
import Icon from "@/components/Icon";

function PageHero({ overline, title, subtitle }) {
  return (
    <section className="relative bg-ink text-white grain pt-40 pb-20 md:pt-48 md:pb-28">
      <div className="relative z-10 mx-auto max-w-7xl px-5 md:px-8">
        <Reveal>
          <span className="overline text-xs uppercase tracking-[0.3em] text-gold font-medium">{overline}</span>
          <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight leading-none max-w-4xl">{title}</h1>
          {subtitle && <p className="mt-6 max-w-xl text-lg text-white/55 font-light">{subtitle}</p>}
        </Reveal>
      </div>
    </section>
  );
}

export default function ServicesPage() {
  return (
    <main data-testid="services-page">
      <PageHero
        overline="Leistungen"
        title="Alles rund um Ihr Objekt."
        subtitle="Zehn Leistungsbereiche – ein zuverlässiger Partner. Entdecken Sie, wie wir Ihr Objekt in Bestform halten."
      />

      <section className="bg-cream py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-5 md:px-8 space-y-20 md:space-y-28">
          {SERVICES.map((s, i) => {
            const reverse = i % 2 === 1;
            return (
              <Reveal key={s.slug}>
                <div className={`grid md:grid-cols-2 gap-8 md:gap-14 items-center ${reverse ? "md:[direction:rtl]" : ""}`}>
                  <div className={`overflow-hidden rounded-3xl bg-ink group ${reverse ? "[direction:ltr]" : ""}`}>
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img src={s.image} alt={s.title} className="h-full w-full object-cover opacity-80 transition-transform duration-700 group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-ink/60 to-transparent" />
                    </div>
                  </div>
                  <div className={reverse ? "[direction:ltr]" : ""}>
                    <div className="flex items-center gap-4 mb-5">
                      <span className="grid h-14 w-14 place-items-center rounded-2xl bg-ink text-gold">
                        <Icon name={s.icon} size={24} />
                      </span>
                      <span className="overline text-xs uppercase tracking-[0.2em] text-gold-dark font-medium">{s.tagline}</span>
                    </div>
                    <h2 className="font-serif text-3xl md:text-4xl tracking-tight text-ink">{s.title}</h2>
                    <p className="mt-4 text-neutral-600 font-light text-lg leading-relaxed">{s.desc}</p>
                    <ul className="mt-6 grid grid-cols-2 gap-x-6 gap-y-3">
                      {s.items.map((it) => (
                        <li key={it} className="flex items-start gap-2.5 text-sm text-neutral-700">
                          <Check size={16} className="text-gold-dark shrink-0 mt-0.5" />
                          {it}
                        </li>
                      ))}
                    </ul>
                    <Link to="/kontakt" className="group mt-8 inline-flex items-center gap-2 text-ink hover:text-gold-dark transition-colors font-medium">
                      Kostenlose Besichtigung
                      <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                  </div>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      <section className="bg-ink text-white grain py-20 md:py-28">
        <div className="relative z-10 mx-auto max-w-4xl px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl tracking-tight">Ihre Leistung nicht dabei?</h2>
            <p className="mt-5 text-white/55 font-light text-lg">Sprechen Sie uns an – wir finden für nahezu jedes Anliegen rund um Ihr Objekt eine Lösung.</p>
            <Link to="/kontakt" className="mt-8 inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 text-ink font-medium hover:bg-gold-light transition-all hover:shadow-[0_0_30px_rgba(201,169,97,0.4)]">
              Jetzt anfragen <ArrowUpRight size={18} />
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
